/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useMemo, useState } from 'react';
import jwtDecode from 'jwt-decode';
const AUTH_TOKEN_KEY = 'authToken';
const clearState = () => {
    try {
        localStorage.removeItem(AUTH_TOKEN_KEY);
    }
    catch (ignore) { }
    return {};
};
const setStateFromTokenResponse = (token, sessionExpirationInMinutes) => {
    try {
        if (token) {
            const now = new Date();
            const expiration = now.setMinutes(now.getMinutes() + sessionExpirationInMinutes);
            const localStorageState = {
                token,
                expires: sessionExpirationInMinutes > 0 ? new Date(expiration) : undefined,
            };
            try {
                localStorage.setItem(AUTH_TOKEN_KEY, JSON.stringify(localStorageState));
                return {
                    token,
                    tokenData: jwtDecode(localStorageState.token),
                };
            }
            catch (e) {
                if (console === null || console === void 0 ? void 0 : console.error) {
                    console.error(e);
                }
            }
        }
        else {
            if (console === null || console === void 0 ? void 0 : console.error) {
                console.error(new Error('No token'));
            }
        }
    }
    catch (e) {
        if (console === null || console === void 0 ? void 0 : console.error) {
            console.error(e);
        }
    }
    // Ensure logged out state is cleared or invalid token state is cleared
    return clearState();
};
const getStateFromCookie = (sessionExpirationInMinutes) => {
    try {
        let prevState = {};
        try {
            prevState = JSON.parse(localStorage.getItem(AUTH_TOKEN_KEY) || '{}');
        }
        catch (ignore) { }
        if (prevState === null || prevState === void 0 ? void 0 : prevState.token) {
            const nextState = setStateFromTokenResponse(prevState.token, sessionExpirationInMinutes);
            return {
                lastLoginDate: (nextState === null || nextState === void 0 ? void 0 : nextState.lastLoginDate) || undefined,
                token: nextState.token,
                tokenData: nextState.token ? jwtDecode(nextState.token) : undefined,
            };
        }
    }
    catch (e) {
        if (console) {
            console.error(e);
        }
    }
    // Ensure logged out state is cleared or invalid token state is cleared
    return clearState();
};
const AuthorizationStore = createContext({
    refreshState: () => null,
    setState: () => null,
    setToken: () => null,
    state: {},
});
const { Provider } = AuthorizationStore;
const AuthorizationProvider = (props) => {
    const envSettings = props.environmentSettings || {};
    const { sessionExpirationInMinutes = 0 } = envSettings.securitySettings || {};
    const [state, setState] = useState(getStateFromCookie(sessionExpirationInMinutes));
    const refreshState = () => {
        return getStateFromCookie(sessionExpirationInMinutes);
    };
    const setToken = (token) => {
        const nextState = setStateFromTokenResponse(token, sessionExpirationInMinutes);
        setState(nextState);
        return nextState;
    };
    return React.createElement(Provider, { value: { refreshState, setState, setToken, state } }, props.children);
};
function useAuthorization() {
    const authContext = useContext(AuthorizationStore);
    // Memoized State
    const isLoggedIn = useMemo(() => {
        var _a, _b;
        return !!((_b = (_a = authContext === null || authContext === void 0 ? void 0 : authContext.state) === null || _a === void 0 ? void 0 : _a.token) === null || _b === void 0 ? void 0 : _b.length);
    }, [authContext.state]);
    const authToken = useMemo(() => {
        return authContext.state.token;
    }, [authContext.state]);
    const tokenData = useMemo(() => {
        return authContext.state.tokenData;
    }, [authContext.state]);
    // Methods
    const getAuthToken = () => { var _a; return ((_a = authContext.refreshState()) === null || _a === void 0 ? void 0 : _a.token) || ''; };
    const getUserId = () => { var _a, _b; return ((_b = (_a = authContext.refreshState()) === null || _a === void 0 ? void 0 : _a.tokenData) === null || _b === void 0 ? void 0 : _b['userData/userId']) || ''; };
    const setToken = (token) => authContext.setToken(token);
    const removeToken = () => authContext.setState(clearState());
    return {
        authToken: authToken || '',
        isLoggedIn: isLoggedIn || false,
        getAuthToken,
        getUserId,
        maintainSetup: !!(tokenData === null || tokenData === void 0 ? void 0 : tokenData['claim/MAINTAIN_SETUP']),
        maintainUsers: !!(tokenData === null || tokenData === void 0 ? void 0 : tokenData['claim/MAINTAIN_USERS']),
        refreshState: authContext.refreshState,
        removeToken,
        setToken,
        tokenData,
        tokenExp: tokenData === null || tokenData === void 0 ? void 0 : tokenData.exp,
        userEmail: (tokenData === null || tokenData === void 0 ? void 0 : tokenData['userData/email']) || '',
        userId: (tokenData === null || tokenData === void 0 ? void 0 : tokenData['userData/userId']) || '',
        userName: (tokenData === null || tokenData === void 0 ? void 0 : tokenData['userData/username']) || '',
    };
}
export { AuthorizationStore, AuthorizationProvider, useAuthorization };
//# sourceMappingURL=provider.js.map